import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Copy, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ApiDocsPage = () => {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const CodeBlock = ({ code, language = 'json' }) => (
    <div className="relative bg-[#1E1E1E] rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-[#2D2D2D] border-b border-[#3D3D3D]">
        <span className="text-xs text-gray-400 font-mono">{language}</span>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-6 text-gray-400 hover:text-white"
          onClick={() => copyToClipboard(code)}
        >
          <Copy className="h-3 w-3 mr-1" />
          Copiar
        </Button>
      </div>
      <pre className="p-4 overflow-x-auto text-sm">
        <code className="text-gray-300 font-mono whitespace-pre">{code}</code>
      </pre>
    </div>
  );

  const Endpoint = ({ method, path, description, children }) => (
    <div className="border border-[#E9EDEF] rounded-lg overflow-hidden mb-6">
      <div className="bg-[#F0F2F5] px-4 py-3 flex items-center space-x-3">
        <Badge className={`font-mono ${
          method === 'POST' ? 'bg-green-500' : 
          method === 'GET' ? 'bg-blue-500' : 
          method === 'DELETE' ? 'bg-red-500' : 'bg-gray-500'
        }`}>
          {method}
        </Badge>
        <code className="text-sm font-mono text-[#2C3E50]">{path}</code>
      </div>
      <div className="p-4">
        <p className="text-[#6B7B8C] mb-4">{description}</p>
        {children}
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="api-docs-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#2C3E50]">Documentación API</h1>
        <p className="text-[#6B7B8C] mt-1">Guía de integración para SmartBoat MessageHub</p>
      </div>

      {/* Base URL */}
      <Card className="bg-white border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-[#2C3E50]">URL Base</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2 bg-[#F0F2F5] p-3 rounded-lg">
            <code className="text-sm font-mono text-[#2C3E50] flex-1">{BACKEND_URL}/api/v1</code>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => copyToClipboard(`${BACKEND_URL}/api/v1`)}
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="auth" className="space-y-6">
        <TabsList className="bg-[#F0F2F5]">
          <TabsTrigger value="auth">Autenticación</TabsTrigger>
          <TabsTrigger value="messages">Mensajes</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="examples">Ejemplos</TabsTrigger>
        </TabsList>

        {/* Auth Tab */}
        <TabsContent value="auth" className="space-y-6">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-[#2C3E50]">Autenticación por API Key</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-[#6B7B8C]">
                Todas las peticiones a la API deben incluir tu API Key en el header <code className="bg-[#F0F2F5] px-2 py-1 rounded">X-API-Key</code>.
              </p>
              <CodeBlock 
                code={`curl -X POST ${BACKEND_URL}/api/v1/messages/send \\
  -H "X-API-Key: smh_tu_api_key_aqui" \\
  -H "Content-Type: application/json" \\
  -d '{"to": "+5491123456789", "message": "Hola!"}'`}
                language="bash"
              />
            </CardContent>
          </Card>

          <Endpoint 
            method="POST" 
            path="/api/v1/auth/token"
            description="Obtiene un token JWT (alternativa a API Key)"
          >
            <p className="text-sm text-[#6B7B8C] mb-2">Request Headers:</p>
            <CodeBlock code={`{
  "X-API-Key": "smh_tu_api_key"
}`} />
            <p className="text-sm text-[#6B7B8C] mt-4 mb-2">Response:</p>
            <CodeBlock code={`{
  "access_token": "eyJhbGciOiJIUzI1NiIs...",
  "token_type": "bearer",
  "expires_in": 86400
}`} />
          </Endpoint>
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages" className="space-y-6">
          <Endpoint 
            method="POST" 
            path="/api/v1/messages/send"
            description="Envía un mensaje individual de WhatsApp"
          >
            <p className="text-sm text-[#6B7B8C] mb-2">Request Body:</p>
            <CodeBlock code={`{
  "to": "+5491123456789",
  "message": "Hola! Este es un mensaje de prueba."
}`} />
            <p className="text-sm text-[#6B7B8C] mt-4 mb-2">Response:</p>
            <CodeBlock code={`{
  "id": "msg_abc123",
  "client_id": "client_xyz",
  "to": "+5491123456789",
  "message": "Hola! Este es un mensaje de prueba.",
  "status": "queued",
  "created_at": "2024-01-15T10:30:00Z"
}`} />
          </Endpoint>

          <Endpoint 
            method="POST" 
            path="/api/v1/messages/send-bulk"
            description="Envía múltiples mensajes de WhatsApp"
          >
            <p className="text-sm text-[#6B7B8C] mb-2">Request Body:</p>
            <CodeBlock code={`{
  "messages": [
    {
      "to": "+5491123456789",
      "message": "Mensaje 1"
    },
    {
      "to": "+5491198765432",
      "message": "Mensaje 2"
    }
  ]
}`} />
            <p className="text-sm text-[#6B7B8C] mt-4 mb-2">Response:</p>
            <CodeBlock code={`{
  "messages": [
    {"message_id": "msg_abc123", "to": "+5491123456789", "status": "queued"},
    {"message_id": "msg_def456", "to": "+5491198765432", "status": "queued"}
  ],
  "total": 2
}`} />
          </Endpoint>

          <Endpoint 
            method="GET" 
            path="/api/v1/messages/{id}/status"
            description="Consulta el estado de un mensaje específico"
          >
            <p className="text-sm text-[#6B7B8C] mb-2">Response:</p>
            <CodeBlock code={`{
  "id": "msg_abc123",
  "status": "delivered",
  "twilio_sid": "SM1234567890",
  "created_at": "2024-01-15T10:30:00Z",
  "updated_at": "2024-01-15T10:30:05Z"
}`} />
            <div className="mt-4">
              <p className="text-sm font-medium text-[#2C3E50] mb-2">Estados posibles:</p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">queued</Badge>
                <Badge variant="outline" className="bg-blue-50">sent</Badge>
                <Badge variant="outline" className="bg-green-50">delivered</Badge>
                <Badge variant="outline" className="bg-emerald-50">read</Badge>
                <Badge variant="outline" className="bg-red-50">failed</Badge>
              </div>
            </div>
          </Endpoint>
        </TabsContent>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks" className="space-y-6">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-[#2C3E50]">Webhooks</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-[#6B7B8C]">
                Configura una URL de webhook para recibir notificaciones en tiempo real sobre el estado de tus mensajes.
              </p>
            </CardContent>
          </Card>

          <Endpoint 
            method="POST" 
            path="/api/v1/webhooks"
            description="Configura la URL de webhook para tu cliente"
          >
            <p className="text-sm text-[#6B7B8C] mb-2">Request Body:</p>
            <CodeBlock code={`{
  "url": "https://tu-servidor.com/webhook/smh",
  "events": ["message.sent", "message.delivered", "message.failed"]
}`} />
          </Endpoint>

          <Card className="bg-white border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-[#2C3E50]">Formato del Webhook</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#6B7B8C] mb-4">
                Cuando ocurre un evento, enviaremos un POST a tu URL con el siguiente formato:
              </p>
              <CodeBlock code={`{
  "event": "message.delivered",
  "data": {
    "message_id": "msg_abc123",
    "status": "delivered"
  },
  "timestamp": "2024-01-15T10:30:05Z"
}`} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Examples Tab */}
        <TabsContent value="examples" className="space-y-6">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-[#2C3E50]">Python</CardTitle>
            </CardHeader>
            <CardContent>
              <CodeBlock 
                language="python"
                code={`import requests

API_URL = "${BACKEND_URL}/api/v1"
API_KEY = "smh_tu_api_key"

def send_whatsapp_message(to: str, message: str):
    response = requests.post(
        f"{API_URL}/messages/send",
        headers={
            "X-API-Key": API_KEY,
            "Content-Type": "application/json"
        },
        json={
            "to": to,
            "message": message
        }
    )
    return response.json()

# Uso
result = send_whatsapp_message("+5491123456789", "Hola desde Python!")
print(f"Message ID: {result['id']}, Status: {result['status']}")`}
              />
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-[#2C3E50]">JavaScript / Node.js</CardTitle>
            </CardHeader>
            <CardContent>
              <CodeBlock 
                language="javascript"
                code={`const API_URL = "${BACKEND_URL}/api/v1";
const API_KEY = "smh_tu_api_key";

async function sendWhatsAppMessage(to, message) {
  const response = await fetch(\`\${API_URL}/messages/send\`, {
    method: 'POST',
    headers: {
      'X-API-Key': API_KEY,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ to, message })
  });
  return response.json();
}

// Uso
sendWhatsAppMessage('+5491123456789', 'Hola desde Node.js!')
  .then(result => console.log('Message ID:', result.id));`}
              />
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg text-[#2C3E50]">cURL</CardTitle>
            </CardHeader>
            <CardContent>
              <CodeBlock 
                language="bash"
                code={`# Enviar mensaje individual
curl -X POST "${BACKEND_URL}/api/v1/messages/send" \\
  -H "X-API-Key: smh_tu_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"to": "+5491123456789", "message": "Hola desde cURL!"}'

# Envío masivo
curl -X POST "${BACKEND_URL}/api/v1/messages/send-bulk" \\
  -H "X-API-Key: smh_tu_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "messages": [
      {"to": "+5491123456789", "message": "Mensaje 1"},
      {"to": "+5491198765432", "message": "Mensaje 2"}
    ]
  }'

# Consultar estado
curl -X GET "${BACKEND_URL}/api/v1/messages/MSG_ID/status" \\
  -H "X-API-Key: smh_tu_api_key"`}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ApiDocsPage;
